import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import * as api from '../services/api';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const settings = await api.getSiteSettings();
        setTheme(settings.theme);
        applyTheme(settings.theme);
      } catch (error) {
        console.error('Error fetching theme:', error);
        // Use default theme if fetch fails
        const defaultTheme = {
          colors: {
            primary: '#7c3aed',
            secondary: '#14b8a6',
            accent: '#8b5cf6',
            background: '#ffffff',
            text_primary: '#111827',
            text_secondary: '#6b7280',
            success: '#10b981',
            warning: '#f59e0b',
            error: '#ef4444'
          },
          typography: {
            font_family: 'Raleway, sans-serif',
            heading_font: 'Raleway, sans-serif',
            body_font: 'Raleway, sans-serif',
            font_size_base: '16px',
            font_size_lg: '18px',
            font_size_xl: '20px'
          },
          border_radius: '0.75rem',
          section_spacing: '5rem'
        };
        setTheme(defaultTheme);
        applyTheme(defaultTheme);
      } finally {
        setLoading(false);
      }
    };

    fetchTheme();
  }, []);

  const applyTheme = (themeData) => {
    if (!themeData) return;

    const root = document.documentElement;

    // Apply color CSS variables
    if (themeData.colors) {
      Object.entries(themeData.colors).forEach(([key, value]) => {
        root.style.setProperty(`--color-${key.replace(/_/g, '-')}`, value);
      });
    }

    // Apply typography CSS variables
    if (themeData.typography) {
      Object.entries(themeData.typography).forEach(([key, value]) => {
        root.style.setProperty(`--${key.replace(/_/g, '-')}`, value);
      });
    }

    // Apply other design tokens
    if (themeData.border_radius) {
      root.style.setProperty('--border-radius', themeData.border_radius);
    }
    if (themeData.section_spacing) {
      root.style.setProperty('--section-spacing', themeData.section_spacing);
    }
  };

  const updateTheme = useCallback((newTheme) => {
    setTheme(newTheme);
    applyTheme(newTheme);
  }, []);

  const contextValue = useMemo(() => ({ theme, loading, updateTheme }), [theme, loading, updateTheme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
